import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Dropdown,
  Message,
  Icon,
  Segment,
  Header,
  Table,
  Label,
  Input
} from 'semantic-ui-react';
import { userService } from '../../services/users/UserService';
import { quizAttemptService, QuizAttemptHistoryDto } from '../../services/learning/QuizAttemptService';
import { UserResponse } from '../../types/dtos';
import { useToast } from '../../hooks/useToast';

interface ManageQuizAttemptsModalProps {
  open: boolean;
  onClose: () => void;
  quizId: string;
  quizTitle: string;
}

const ManageQuizAttemptsModal: React.FC<ManageQuizAttemptsModalProps> = ({
  open,
  onClose,
  quizId,
  quizTitle
}) => {
  const { showSuccess, showError } = useToast();
  
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [userHistory, setUserHistory] = useState<QuizAttemptHistoryDto[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [extraAttempts, setExtraAttempts] = useState<number>(1);
  const [granting, setGranting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  useEffect(() => {
    if (selectedUserId) {
      loadUserHistory();
    } else {
      setUserHistory([]);
    }
  }, [selectedUserId]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await userService.getUsers({ pageSize: 1000 });
      // Verificar se é um array direto ou um objeto paginado
      if (Array.isArray(response)) {
        setUsers(response);
      } else if (response && 'items' in response) {
        setUsers(response.items || []);
      } else {
        setUsers([]);
      }
    } catch (err: any) {
      showError('Erro ao carregar usuários');
      console.error(err);
      setUsers([]); // Garantir que sempre seja um array
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadUserHistory = async () => {
    if (!selectedUserId) return;

    try {
      setLoadingHistory(true);
      const history = await quizAttemptService.getUserQuizAttemptHistory(selectedUserId);
      
      // Garantir que history seja um array
      const historyArray = Array.isArray(history) ? history : [];
      
      // Filtrar apenas tentativas deste quiz (comparação case-insensitive para IDs)
      const quizHistory = historyArray.filter((h: QuizAttemptHistoryDto) => {
        const hQuizId = String(h.quizId || '').toLowerCase();
        const currentQuizId = String(quizId || '').toLowerCase();
        return hQuizId === currentQuizId;
      });
      
      setUserHistory(quizHistory || []);
    } catch (err: any) {
      showError('Erro ao carregar histórico de tentativas');
      console.error('Erro ao carregar histórico:', err);
      setUserHistory([]); // Garantir que sempre seja um array
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGrantAttempts = async () => {
    if (!selectedUserId) {
      showError('Selecione um usuário');
      return;
    }

    if (extraAttempts <= 0) {
      showError('O número de tentativas extras deve ser maior que zero');
      return;
    }

    try {
      setGranting(true);
      await quizAttemptService.grantExtraAttempts(quizId, selectedUserId, extraAttempts);
      showSuccess(`${extraAttempts} tentativa(s) extra(s) concedida(s) com sucesso!`);
      await loadUserHistory(); // Recarregar histórico
      setExtraAttempts(1); // Resetar valor
    } catch (err: any) {
      showError(err.message || 'Erro ao conceder tentativas extras');
      console.error(err);
    } finally {
      setGranting(false);
    }
  };

  const handleClose = () => {
    setSelectedUserId('');
    setUserHistory([]);
    setExtraAttempts(1);
    setSearchQuery('');
    onClose();
  };

  const userOptions = (users || []).map(user => ({
    key: user.id,
    value: user.id,
    text: `${user.nome} (${user.email})`,
    content: (
      <div>
        <div style={{ fontWeight: 'bold' }}>{user.nome}</div>
        <div style={{ fontSize: '0.9em', color: '#666' }}>{user.email}</div>
      </div>
    )
  }));

  const getStatusLabel = (status: number) => {
    switch (status) {
      case 1: // InProgress
        return <Label color="yellow" size="small">Em Andamento</Label>;
      case 2: // Completed
        return <Label color="green" size="small">Concluída</Label>;
      case 3: // Abandoned
        return <Label color="grey" size="small">Abandonada</Label>;
      default:
        return <Label size="small">Desconhecido</Label>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}min ${remainingSeconds}s`;
  };

  return (
    <>
      <style>{`
        .manage-quiz-attempts-dropdown .ui.dropdown .menu {
          z-index: 1001 !important;
          max-height: 300px;
          overflow-y: auto;
        }
        .ui.modal.manage-quiz-attempts-modal {
          max-height: 90vh !important;
        }
        .ui.modal.manage-quiz-attempts-modal .scrolling.content {
          max-height: calc(90vh - 150px) !important;
          overflow-y: auto !important;
        }
      `}</style>
      <Modal 
        open={open} 
        onClose={handleClose} 
        size="large"
        className="manage-quiz-attempts-modal"
        style={{ maxHeight: '90vh', height: 'auto' }}
      >
      <Modal.Header>
        <Icon name="clipboard check" />
        Gerenciar Tentativas - {quizTitle}
      </Modal.Header>
      
      <Modal.Content scrolling style={{ maxHeight: 'calc(90vh - 150px)', overflowY: 'auto' }}>
        <Segment>
          <Header as="h4">Selecionar Usuário</Header>
          <Form>
            <Form.Field>
              <label>Buscar Usuário</label>
              <Dropdown
                placeholder="Digite o nome ou email do usuário"
                fluid
                search
                selection
                loading={loadingUsers}
                options={userOptions}
                value={selectedUserId}
                onChange={(e, { value }) => setSelectedUserId(value as string)}
                searchQuery={searchQuery}
                onSearchChange={(e, { searchQuery }) => setSearchQuery(searchQuery)}
                style={{ zIndex: 1000 }}
                className="manage-quiz-attempts-dropdown"
              />
            </Form.Field>
          </Form>
        </Segment>

        {selectedUserId && (
          <>
            <Segment>
              <Header as="h4">Histórico de Tentativas</Header>
              {loadingHistory ? (
                <Message info>
                  <Icon name="spinner" loading />
                  Carregando histórico...
                </Message>
              ) : userHistory.length === 0 ? (
                <Message info>
                  <Message.Header>Nenhuma tentativa registrada</Message.Header>
                  <p>Este usuário ainda não realizou nenhuma tentativa neste questionário.</p>
                </Message>
              ) : (
                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Tentativa</Table.HeaderCell>
                      <Table.HeaderCell>Data</Table.HeaderCell>
                      <Table.HeaderCell>Duração</Table.HeaderCell>
                      <Table.HeaderCell>Pontuação</Table.HeaderCell>
                      <Table.HeaderCell>Status</Table.HeaderCell>
                      <Table.HeaderCell>Resultado</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {(userHistory || []).map((attempt, index) => (
                      <Table.Row key={attempt.attemptId}>
                        <Table.Cell>#{attempt.attemptNumber || index + 1}</Table.Cell>
                        <Table.Cell>{formatDate(attempt.startedAt)}</Table.Cell>
                        <Table.Cell>{formatDuration(attempt.timeSpentSeconds)}</Table.Cell>
                        <Table.Cell>
                          {attempt.score !== null && attempt.score !== undefined 
                            ? `${attempt.score.toFixed(1)}%` 
                            : '-'}
                        </Table.Cell>
                        <Table.Cell>{getStatusLabel(attempt.status)}</Table.Cell>
                        <Table.Cell>
                          {attempt.isPassed !== null && attempt.isPassed !== undefined ? (
                            attempt.isPassed ? (
                              <Label color="green" size="small">
                                <Icon name="check" />
                                Aprovado
                              </Label>
                            ) : (
                              <Label color="red" size="small">
                                <Icon name="x" />
                                Reprovado
                              </Label>
                            )
                          ) : (
                            '-'
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
              
              <Message info size="small">
                <strong>Total de tentativas:</strong> {userHistory.length}
              </Message>
            </Segment>

            <Segment>
              <Header as="h4">Conceder Tentativas Extras</Header>
              <Form>
                <Form.Group widths="equal">
                  <Form.Field>
                    <label>Número de Tentativas Extras</label>
                    <Input
                      type="number"
                      min="1"
                      value={extraAttempts}
                      onChange={(e) => setExtraAttempts(Number(e.target.value))}
                      placeholder="Digite o número de tentativas"
                    />
                  </Form.Field>
                  <Form.Field style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button
                      primary
                      fluid
                      loading={granting}
                      disabled={granting || !selectedUserId}
                      onClick={handleGrantAttempts}
                    >
                      <Icon name="plus" />
                      Conceder Tentativas
                    </Button>
                  </Form.Field>
                </Form.Group>
              </Form>
              
              <Message warning size="small">
                <Icon name="info circle" />
                Ao conceder tentativas extras, o usuário poderá refazer o questionário mesmo que tenha atingido o limite de tentativas.
              </Message>
            </Segment>
          </>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={handleClose}>
          <Icon name="close" />
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
    </>
  );
};

export default ManageQuizAttemptsModal;

